/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormLabel,
  Typography,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { apipost, apiget } from '../../../service/api';

const ComposeEmail = ({ open, handleClose, leadId, leadData, onSuccess, apiEndpoint, extraPayload }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Fetch SendGrid templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const result = await apiget('communication/sendgrid-templates');
        if (result && result.status === 200) {
          setTemplates(result.data.result || []);
        }
      } catch (err) {
        console.error('Failed to fetch SendGrid templates:', err);
      } finally {
        setLoadingTemplates(false);
      }
    };
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  // Fetch full HTML preview when template changes
  useEffect(() => {
    if (!selectedTemplate) {
      setPreviewHtml('');
      setShowPreview(false);
      return;
    }
    const fetchPreview = async () => {
      try {
        setLoadingPreview(true);
        const result = await apiget(`communication/sendgrid-templates/${selectedTemplate}/preview`);
        if (result && result.status === 200) {
          setPreviewHtml(result.data.result?.html_content || '');
          setShowPreview(true);
        }
      } catch (err) {
        console.error('Failed to fetch template preview:', err);
        setPreviewHtml('');
      } finally {
        setLoadingPreview(false);
      }
    };
    fetchPreview();
  }, [selectedTemplate]);

  // Apply merge fields
  const applyMergeFields = (text) => {
    if (!text) return text;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return text
      .replace(/\{firstName\}/g, leadData?.firstName || '')
      .replace(/\{lastName\}/g, leadData?.lastName || '')
      .replace(/\{loanOfficerName\}/g, `${user.firstName || ''} ${user.lastName || ''}`.trim());
  };

  const isTemplateSelected = Boolean(selectedTemplate);

  const validationSchema = yup.object({
    to: yup.string().email('Enter a valid email').required('Recipient email is required'),
    subject: isTemplateSelected
      ? yup.string()
      : yup.string().required('Subject is required'),
    message: isTemplateSelected
      ? yup.string()
      : yup.string().required('Message is required'),
  });

  const formik = useFormik({
    initialValues: {
      to: leadData?.emailAddress || '',
      subject: '',
      message: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setSending(true);
        const user = JSON.parse(localStorage.getItem('user') || '{}');

        const payload = {
          leadId,
          to: values.to,
          subject: applyMergeFields(values.subject) || undefined,
          message: applyMergeFields(values.message) || undefined,
          ...(extraPayload || {}),
        };

        // If a SendGrid template is selected, include template data
        if (selectedTemplate) {
          payload.templateId = selectedTemplate;
          payload.dynamicTemplateData = {
            firstName: leadData?.firstName || '',
            lastName: leadData?.lastName || '',
            name: `${leadData?.firstName || ''} ${leadData?.lastName || ''}`.trim(),
            email: leadData?.emailAddress || '',
            mobile: leadData?.phoneNumber || '',
            loanOfficerName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            subject: applyMergeFields(values.subject) || undefined,
          };
        }

        const result = await apipost(apiEndpoint || 'communication/send-email', payload);
        if (result && result.status === 200) {
          toast.success('Email sent successfully!');
          resetForm();
          setSelectedTemplate('');
          setPreviewHtml('');
          setShowPreview(false);
          handleClose();
          if (onSuccess) onSuccess(result);
        }
      } catch (error) {
        console.error('Error sending email:', error);
        toast.error('Failed to send email.');
      } finally {
        setSending(false);
      }
    },
  });

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplate(templateId);
    if (templateId) {
      const tmpl = templates.find((t) => t.id === templateId);
      if (tmpl && tmpl.subject) {
        formik.setFieldValue('subject', tmpl.subject);
      }
    }
  };

  const handleDialogClose = () => {
    formik.resetForm();
    setSelectedTemplate('');
    setPreviewHtml('');
    setShowPreview(false);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Compose Email</Typography>
          <IconButton onClick={handleDialogClose} size="small">
            <ClearIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <form onSubmit={formik.handleSubmit}>
          <Grid container rowSpacing={3} columnSpacing={{ xs: 0, sm: 5, md: 4 }}>
            {/* Template Selector */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>SendGrid Template (Optional)</InputLabel>
                <Select
                  value={selectedTemplate}
                  onChange={handleTemplateChange}
                  label="SendGrid Template (Optional)"
                  disabled={loadingTemplates}
                >
                  <MenuItem value="">
                    <em>None - Plain Text Email</em>
                  </MenuItem>
                  {templates.map((tmpl) => (
                    <MenuItem key={tmpl.id} value={tmpl.id}>
                      {tmpl.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Template Preview */}
            {isTemplateSelected && (
              <Grid item xs={12}>
                <Alert severity="info" variant="outlined" sx={{ py: 0, mb: 1 }}>
                  This email will use the SendGrid template design. Subject and message below are optional overrides.
                </Alert>

                {loadingPreview ? (
                  <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={24} />
                  </Box>
                ) : previewHtml ? (
                  <>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Template Preview
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setShowPreview(!showPreview)}
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {showPreview ? 'Hide Preview' : 'Show Preview'}
                      </Button>
                    </Box>
                    {showPreview && (
                      <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1 }}>
                        <iframe
                          title="Email Template Preview"
                          srcDoc={previewHtml}
                          style={{
                            width: '100%',
                            height: '400px',
                            border: 'none',
                          }}
                          sandbox="allow-same-origin"
                        />
                      </Paper>
                    )}
                  </>
                ) : null}
              </Grid>
            )}

            {/* To */}
            <Grid item xs={12} sm={6}>
              <FormLabel>To</FormLabel>
              <TextField
                id="to"
                name="to"
                size="small"
                fullWidth
                value={formik.values.to}
                onChange={formik.handleChange}
                error={formik.touched.to && Boolean(formik.errors.to)}
                helperText={formik.touched.to && formik.errors.to}
              />
            </Grid>

            {/* From (read-only) */}
            <Grid item xs={12} sm={6}>
              <FormLabel>From</FormLabel>
              <TextField
                size="small"
                fullWidth
                value="taylor.morris@jethl.com"
                disabled
              />
            </Grid>

            {/* Subject */}
            <Grid item xs={12}>
              <FormLabel>Subject{isTemplateSelected ? ' (Optional)' : ''}</FormLabel>
              <TextField
                id="subject"
                name="subject"
                size="small"
                fullWidth
                value={formik.values.subject}
                onChange={formik.handleChange}
                error={formik.touched.subject && Boolean(formik.errors.subject)}
                helperText={formik.touched.subject && formik.errors.subject}
              />
            </Grid>

            {/* Message */}
            <Grid item xs={12}>
              <FormLabel>Message{isTemplateSelected ? ' (Optional)' : ''}</FormLabel>
              <TextField
                id="message"
                name="message"
                size="small"
                fullWidth
                multiline
                rows={8}
                value={formik.values.message}
                onChange={formik.handleChange}
                error={formik.touched.message && Boolean(formik.errors.message)}
                helperText={formik.touched.message && formik.errors.message}
              />
              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                Use {'{firstName}'}, {'{lastName}'}, {'{loanOfficerName}'} for merge fields.
              </Typography>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleDialogClose}
          variant="outlined"
          color="error"
          sx={{ textTransform: 'capitalize' }}
        >
          Cancel
        </Button>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          color="secondary"
          disabled={sending}
          startIcon={sending ? <CircularProgress size={16} /> : null}
          sx={{ textTransform: 'capitalize' }}
        >
          {sending ? 'Sending...' : 'Send Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComposeEmail;
